<script setup lang="ts">
/* DESIGN.md § 4 Buttons — 4 intent variants + utility (icon-only).
   variant:
     primary    Vodafone Red — page's #1 action
     authority  KAG Navy — institutional / non-destructive primary
     secondary  white outlined
     tertiary   text-only (signal blue)
     destructive  white + critical border
     ghost      transparent, charcoal — quiet inline action */
import { computed } from 'vue';
import { useReadonly } from '@/composables/useReadonly';

type Variant = 'primary' | 'authority' | 'secondary' | 'tertiary' | 'destructive' | 'ghost';
const props = withDefaults(
  defineProps<{ variant?: Variant; size?: 'sm' | 'md'; ownGate?: boolean }>(),
  { variant: 'secondary', size: 'md', ownGate: false },
);

// 읽기전용 agent_admin 에게는 액션 CTA(생성/저장/제출/삭제 계열)를 숨긴다.
// primary/authority/destructive = 쓰기 의도 버튼. secondary/tertiary/ghost 는
// 네비게이션·닫기 등에 섞여 쓰이므로 숨기지 않는다(읽기 흐름 보존). 블로그는 예외.
// ownGate: 화면이 경로 키와 다른 권한으로 이 버튼을 직접 가리는 경우(탭마다 주인이
// 다른 Commission Setting Confirm 등) — 경로 키로 숨기면 그 탭 주인이 확정을 못 한다.
const { hideActions } = useReadonly();
const hidden = computed(
  () =>
    !props.ownGate &&
    hideActions.value &&
    ['primary', 'authority', 'destructive'].includes(props.variant),
);

const sizeClass = props.size === 'sm' ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm';

const variantClass: Record<Variant, string> = {
  primary:
    'bg-vodafone-red text-white border-transparent hover:bg-vodafone-red-hover active:bg-vodafone-red-active disabled:bg-vodafone-red-disabled disabled:cursor-not-allowed',
  authority:
    'bg-kag-navy text-white border-transparent hover:bg-kag-navy-hover active:bg-kag-navy-active disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'bg-canvas text-charcoal-body border-border-strong hover:bg-light-neutral hover:border-kag-navy active:bg-hairline disabled:opacity-50 disabled:cursor-not-allowed',
  tertiary:
    'bg-transparent text-kag-navy border-transparent hover:bg-info-soft active:bg-info-soft/80 disabled:opacity-50 disabled:cursor-not-allowed',
  destructive:
    'bg-canvas text-critical border-critical hover:bg-critical-soft active:bg-critical-soft/80 disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'bg-transparent text-charcoal-body border-transparent hover:bg-light-neutral active:bg-hairline disabled:opacity-50 disabled:cursor-not-allowed',
};
</script>

<template>
  <button
    v-if="!hidden"
    type="button"
    class="inline-flex items-center justify-center gap-2 border rounded-utility font-bold uppercase tracking-[0.2px] transition-colors"
    :class="[sizeClass, variantClass[props.variant]]"
  >
    <slot />
  </button>
</template>
